import { Modal } from "antd";

import { DesignTemplate } from "@/api/config/chalice-types";
import { genericError, UseStateReturn } from "@/fixtures/globalConstants";
import {
  addMediaUrlToDesignTemplate,
  createDesignTemplateFromMediaUrls,
} from "@/utils/designTemplateUtils";
import { isImageUrl } from "@/utils/urlUtils";
import openAiLogoUrl from "assets/icons/openai-logo.svg";

import { UploadMedia } from "@/components/UploadMedia/UploadMedia";

const UnsplashIcon = () => (
  <img
    src="https://www.svgrepo.com/show/306907/unsplash.svg"
    alt="Unsplash Logo"
    width={20}
    height={20}
  />
);

const GiphyIcon = () => (
  <img
    src="https://www.svgrepo.com/show/330504/giphy.svg"
    alt="Giphy Logo"
    width={20}
    height={20}
    style={{ objectFit: "contain" }}
  />
);

const OpenAiIcon = () => (
  <img
    src={openAiLogoUrl}
    alt="OpenAI Logo"
    width={20}
    height={20}
    style={{ objectFit: "contain" }}
  />
);

type CustomPostMediaProps = {
  mediaUrlsState: UseStateReturn<string[]>;
  templateState: UseStateReturn<DesignTemplate | undefined>;
  onClose: ({
    cancelled,
    openDesignEditor,
  }: {
    cancelled?: boolean;
    openDesignEditor?: boolean;
  }) => void;
  isOpen: boolean;
};

const AddMediaModal = ({
  mediaUrlsState,
  templateState,
  onClose,
  isOpen,
}: CustomPostMediaProps) => {
  const [mediaUrls, setMediaUrls] = mediaUrlsState;
  const [template, setTemplate] = templateState;

  const processUploadedMedia = async (newUrls: string[]) => {
    try {
      const allNewUrlsAreImages =
        newUrls.length > 0 && newUrls.every(isImageUrl);
      const initialMediaCount = mediaUrls.length;

      if (template) {
        if (allNewUrlsAreImages) {
          let currentTemplate = template;
          for (const mediaUrl of newUrls) {
            currentTemplate = await addMediaUrlToDesignTemplate(
              currentTemplate,
              mediaUrl
            );
          }
          setMediaUrls([...mediaUrls, ...newUrls]);
          setTemplate(currentTemplate);
          onClose({ openDesignEditor: true });
        } else {
          setTemplate(undefined);
          setMediaUrls([...mediaUrls, ...newUrls]);
          onClose({ openDesignEditor: false });
        }
      } else {
        if (initialMediaCount === 0 && allNewUrlsAreImages) {
          const newTemplate = await createDesignTemplateFromMediaUrls(newUrls);
          setMediaUrls(newUrls);
          setTemplate(newTemplate);
          onClose({ openDesignEditor: true });
        } else {
          setMediaUrls((prevUrls) => [...prevUrls, ...newUrls]);
          onClose({ openDesignEditor: false });
        }
      }
    } catch (error) {
      console.error("[processUploadedMedia] Error processing uploads:", error);
      genericError("Failed to process uploaded media. Please try again.");
    }
  };

  const modalContent = (
    <div className="h-auto w-full flex flex-col items-center justify-center">
      <UploadMedia
        mediaUrlsState={mediaUrlsState}
        templateState={templateState}
        onUploadComplete={processUploadedMedia}
      />
    </div>
  );

  return (
    <Modal
      title="Add Media"
      onCancel={() => {
        onClose({ cancelled: true });
      }}
      footer={null}
      width={450}
      centered
      styles={{ body: { padding: "24px" } }}
      open={isOpen}
      keyboard
    >
      {modalContent}
    </Modal>
  );
};

export default AddMediaModal;
